import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import useRetailerApi from '@/useRetailerApi'
import useTranslation from '@/hooks/useTranslation'
import usePageTitle from '@/hooks/usePageTitle'
import useProductSearch from '@/hooks/useProductSearch'
import { ChevronLeft, ChevronRight, SearchLg, XClose } from '@untitledui/icons'
import OptimizedImage from '@/components/OptimizedImage'
import ProductCard from '@/components/ProductCard'
import ProductModal from '@/components/ProductModal'

function Dashboard() {
    const { retailer } = useRetailerApi()
    const { t } = useTranslation()
    usePageTitle(t('nav.products'))
    const [selectedType, setSelectedType] = useState('watches')
    const [selectedCollectionId, setSelectedCollectionId] = useState(null)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [selectedVariantId, setSelectedVariantId] = useState(null)
    const [query, setQuery] = useState('')
    const { isSearching, results } = useProductSearch(query)

    // `variantId` n'est renseigné que depuis un résultat de recherche par SKU :
    // il ouvre la modale directement sur la bonne variante.
    const openProduct = (product, variantId = null) => {
        setSelectedProduct(product)
        setSelectedVariantId(variantId)
    }

    const handleTypeClick = (type) => {
        setSelectedType(type)
        setSelectedCollectionId(null)
    }

    const handleCollectionClick = (collectionId) => {
        setSelectedCollectionId(
            selectedCollectionId === collectionId ? null : collectionId
        )
    }

    const scrollRef = useRef(null)
    const isScrolling = useRef(false)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const checkOverflow = useCallback(() => {
        if (isScrolling.current) return
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 0)
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }, [])

    // `isSearching` masque le carrousel : ses dimensions sont nulles tant qu'il est
    // caché, il faut donc les remesurer quand il réapparaît.
    useEffect(() => {
        checkOverflow()
        const el = scrollRef.current
        el?.addEventListener('scroll', checkOverflow)
        window.addEventListener('resize', checkOverflow)
        return () => {
            el?.removeEventListener('scroll', checkOverflow)
            window.removeEventListener('resize', checkOverflow)
        }
    }, [checkOverflow, selectedType, isSearching])

    const scroll = (direction) => {
        const el = scrollRef.current
        if (!el) return

        const delta = direction * 300
        const futureScroll = Math.max(
            0,
            Math.min(el.scrollLeft + delta, el.scrollWidth - el.clientWidth)
        )
        setCanScrollLeft(futureScroll > 0)
        setCanScrollRight(futureScroll < el.scrollWidth - el.clientWidth - 1)

        isScrolling.current = true
        el.scrollBy({ left: delta, behavior: 'smooth' })

        let timeout
        const onScrollEnd = () => {
            clearTimeout(timeout)
            timeout = setTimeout(() => {
                isScrolling.current = false
                el.removeEventListener('scroll', onScrollEnd)
                checkOverflow()
            }, 50)
        }
        el.addEventListener('scroll', onScrollEnd)
    }

    return (
        <div className="flex flex-col px-[24px] lg:px-[48px] py-[100px] lg:py-[172px] mx-auto">
            <div className="flex flex-col gap-[20px] pb-[60px] lg:pb-[100px]">
                <div className="flex flex-col lg:flex-row lg:items-center items-start gap-[8px] lg:gap-[24px] ">
                    {['watches', 'accessories'].map((type) => {
                        const isActive = selectedType === type && !isSearching
                        const typeClass = clsx(
                            'text-[40px] font-semibold uppercase transition-colors',
                            isSearching && 'cursor-default',
                            !isSearching && 'cursor-pointer',
                            isActive && 'text-dark-blue',
                            !isActive && 'text-dark-blue-10',
                            !isActive && !isSearching && 'hover:text-dark-blue-20'
                        )
                        return (
                            <button
                                key={type}
                                disabled={isSearching}
                                onClick={() => handleTypeClick(type)}
                                className={typeClass}
                            >
                                {t(`dashboard.${type}`)}
                            </button>
                        )
                    })}

                    <div className="relative w-full lg:w-[320px] lg:ml-auto">
                        <SearchLg
                            size={18}
                            className="absolute left-[14px] top-1/2 -translate-y-1/2 text-dark-blue-40 pointer-events-none"
                        />
                        <input
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(event) =>
                                event.key === 'Escape' && setQuery('')
                            }
                            placeholder={t('dashboard.searchPlaceholder')}
                            aria-label={t('dashboard.searchPlaceholder')}
                            className="w-full h-[45px] pl-[42px] pr-[42px] rounded-lg bg-dark-blue/4 border border-transparent text-[16px] text-dark-blue placeholder:text-dark-blue-40 outline-none transition-[border-color] duration-200 focus:border-[#3F81F7]"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => setQuery('')}
                                aria-label={t('dashboard.searchClear')}
                                className="absolute right-0 top-0 h-full px-[14px] flex items-center text-dark-blue-40 hover:text-dark-blue transition-colors cursor-pointer"
                            >
                                <XClose size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <div className={clsx('relative', isSearching && 'hidden')}>
                    <div
                        ref={scrollRef}
                        className="flex overflow-scroll w-full gap-[8px] hide-scrollbar"
                    >
                        {retailer.collections
                            .filter((c) => c.type === selectedType)
                            .map((collection) => {
                                const isSelected =
                                    selectedCollectionId === collection.id
                                const isDimmed =
                                    selectedCollectionId !== null && !isSelected
                                const isAccessories =
                                    selectedType === 'accessories'
                                const collectionClass = clsx(
                                    'flex shrink-0 flex-col gap-[8px] hover:cursor-pointer group text-left transition-opacity',
                                    isAccessories && 'w-[162px]',
                                    !isAccessories && 'w-[131px]',
                                    isDimmed && 'opacity-40',
                                    !isDimmed && 'opacity-100'
                                )
                                return (
                                    <button
                                        key={collection.id}
                                        onClick={() =>
                                            handleCollectionClick(collection.id)
                                        }
                                        className={collectionClass}
                                    >
                                        <OptimizedImage
                                            className={clsx(
                                                'bg-gray-100 rounded-[8px] object-contain p-[30px] group-hover:opacity-90 transition-all',
                                                isAccessories &&
                                                    'aspect-185/200',
                                                !isAccessories && 'aspect-3/4'
                                            )}
                                            src={collection.image_url}
                                            alt={collection.name}
                                            sizes="(max-width: 768px) 150px, (max-width: 1024px) 200px, 312px"
                                            widths={[150, 200, 312, 624]}
                                        />

                                        <span className="text-[14px] uppercase truncate leading-none text-dark-blue-20">
                                            {collection.name}
                                        </span>
                                    </button>
                                )
                            })}
                    </div>

                    <AnimatePresence>
                        {canScrollLeft && (
                            <motion.button
                                key="scroll-left"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                onClick={() => scroll(-1)}
                                className="absolute -left-[17px] top-[70px] w-[34px] h-[34px] bg-white border border-[#E5E5E5] rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </motion.button>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {canScrollRight && (
                            <motion.button
                                key="scroll-right"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                onClick={() => scroll(1)}
                                className="absolute -right-[17px] top-[70px] w-[34px] h-[34px] bg-white border border-[#E5E5E5] rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                                <ChevronRight size={20} />
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <div className="flex flex-col gap-[100px]">
                {isSearching ? (
                    results.length === 0 ? (
                        <p className="text-dark-blue-40 uppercase text-[14px]">
                            {t('dashboard.searchEmpty')}
                        </p>
                    ) : (
                        <div className="flex flex-col gap-[20px]">
                            <div className="mb-[20px] flex justify-between items-end">
                                <span className="text-[22px] uppercase leading-none">
                                    {t('dashboard.searchResults', {
                                        count: results.length,
                                    })}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-[9px] gap-y-[60px]">
                                {results.map(({ product, variantId }) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        onClick={() =>
                                            openProduct(product, variantId)
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    )
                ) : (
                    retailer.collections
                        .filter((collection) => collection.type === selectedType)
                        .filter((collection) =>
                            selectedCollectionId === null
                                ? true
                                : collection.id === selectedCollectionId
                        )
                        .map((collection) => (
                            <div
                                key={collection.id}
                                className="flex flex-col gap-[20px]"
                            >
                                <div className="mb-[20px] flex justify-between items-end">
                                    <span className="text-[22px] uppercase leading-none">
                                        {collection.name}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-[9px] gap-y-[60px]">
                                    {collection.products.map((product) => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            onClick={() => openProduct(product)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                )}
            </div>

            <ProductModal
                product={selectedProduct}
                isOpen={!!selectedProduct}
                initialVariantId={selectedVariantId}
                onClose={() => {
                    setSelectedProduct(null)
                    setSelectedVariantId(null)
                }}
            />
        </div>
    )
}

export default Dashboard
